import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ShieldCheck, Send, AlertCircle, Clock, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import VenueLookup from './VenueLookup';
import './VenueLookup.css';
import './VerificationForm.css';

/**
 * VerificationForm — "Submit for verification" (W-2 Verification Funnel v2).
 *
 * Lives in Club Admin → Club Sponsors while a club is unverified; the tour's
 * final CTA and the hub banner both land here (`/admin?tab=sponsors`).
 * Required: the admin's role at the club. Everything else is optional but
 * nudged — each field says why it speeds verification up. The submitter's
 * name/email come from the account; the address pre-fills from the wizard's
 * home ground and can be corrected here.
 *
 * Submitting fires THE staff verification email (creation is silent now).
 * Resubmission while unverified is allowed — last submission wins.
 */
const fromClubGround = (c) => (c && c.home_ground_lat != null && c.home_ground_lng != null && c.home_ground_address)
  ? { label: c.home_ground_name || '', line: c.home_ground_address, lat: Number(c.home_ground_lat), lng: Number(c.home_ground_lng), locality: null, region: null, kind: 'existing' }
  : null;

const VerificationForm = ({ clubSlug, club, compact = false }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: current, isLoading } = useQuery({
    queryKey: ['verification', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/verification`).then(r => r.data),
  });

  const submitted = !!current?.verification_submitted_at;
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState(null);
  const [addressTouched, setAddressTouched] = useState(false);
  const [error, setError] = useState('');
  const [justSent, setJustSent] = useState(false);

  // Pre-fill ONCE per loaded submission. Both queries refetch on window
  // focus once stale, and an admin who tabs out to copy the club's website
  // URL must not come back to a wiped form (marakin-qa, slice 1) — so the
  // effect is keyed on the submission timestamp, not on object identity.
  const prefilledFor = useRef(null);
  useEffect(() => {
    if (!current) return;
    const key = current.verification_submitted_at || 'none';
    if (prefilledFor.current === key) return;
    prefilledFor.current = key;
    setRole(current.verification_role || '');
    setWebsite(current.website || club?.website || '');
    setPhone(current.verification_contact_phone || '');
    setNotes(current.verification_notes || '');
    setAddress(fromClubGround(current) || fromClubGround(club));
    setAddressTouched(false);
  }, [current, club]);

  const submit = useMutation({
    mutationFn: () => axios.post(`/clubs/${clubSlug}/verification/submit`, {
      role: role.trim(),
      website: website.trim(),
      phone: phone.trim(),
      notes: notes.trim(),
      // Only send the address when the admin changed it — the existing ground
      // is already on the club, and re-sending a 'kind: existing' value would
      // be a no-op round trip.
      ...(addressTouched && address && address.kind !== 'existing' ? { address } : {}),
    }).then(r => r.data),
    onSuccess: () => {
      setError('');
      setEditing(false);
      setJustSent(true);
      qc.invalidateQueries({ queryKey: ['verification', clubSlug] });
      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
      // An address change cascades to future home fixtures server-side —
      // same refresh the Club Settings sibling does.
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
    },
    onError: (e) => setError(e.response?.data?.message || e.response?.data?.error || 'Something went wrong — please try again.'),
  });

  if (isLoading) return null;

  const showForm = !submitted || editing;
  const submittedOn = submitted ? new Date(current.verification_submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;

  return (
    <div className={`vf ${compact ? 'vf-compact' : ''}`} data-tour="verification-form">
      {!showForm && (
        <div className="vf-status">
          <div className="vf-status-head">
            <Clock size={18} />
            <div>
              <strong>{justSent ? 'Sent — thank you.' : 'Submitted for verification'}</strong>
              <span>
                {justSent ? 'We' : `Submitted ${submittedOn}. We`}'ll check the details with the club and be in touch
                {phone || current.verification_contact_phone ? ' — usually a phone call' : ' by email'}, normally within a day or two.
                Your sponsorship tools switch on the moment we verify you.
              </span>
            </div>
          </div>
          <dl className="vf-status-details">
            <dt>Submitted by</dt><dd>{current.submitted_by_name || `${user?.firstName || ''} ${user?.lastName || ''}`} · {current.verification_role}</dd>
            <dt>Club address</dt><dd>{[current.home_ground_name, current.home_ground_address].filter(Boolean).join(' — ') || '—'}</dd>
            <dt>Website / social</dt><dd>{current.website || '—'}</dd>
            {current.verification_contact_phone && <><dt>Phone</dt><dd>{current.verification_contact_phone}</dd></>}
          </dl>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditing(true); setJustSent(false); }}>
            <Pencil size={13} /> Edit details
          </button>
        </div>
      )}

      {showForm && (
        <form className="vf-form" onSubmit={(e) => { e.preventDefault(); if (role.trim().length < 2) { setError('Tell us your role at the club — it\'s how we check with them.'); return; } submit.mutate(); }}>
          <div className="vf-row">
            <div className="form-group">
              <label className="label">Your name</label>
              <input className="input" value={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()} readOnly />
              <p className="vf-help">This is who we'll speak to.</p>
            </div>
            <div className="form-group">
              <label className="label" htmlFor="vf-role">Your role at the club <span className="vf-req">required</span></label>
              <input id="vf-role" className="input" value={role} onChange={e => setRole(e.target.value)} maxLength={80}
                placeholder="e.g. Chair, Secretary, Head Coach, Team Manager" autoFocus={!compact} />
              <p className="vf-help">We check with the club that you hold this role.</p>
            </div>
          </div>

          <div className="form-group">
            <label className="label">Club address</label>
            <VenueLookup value={address} onChange={(v) => { setAddress(v); setAddressTouched(true); }}
              placeholder={`e.g. ${club?.name || 'your club'}, or a postcode`} />
            <p className="vf-help">Also your home ground — correct it here if the wizard picked the wrong spot.</p>
          </div>

          <div className="form-group">
            <label className="label" htmlFor="vf-website">Club website or social page <span className="vf-opt">optional</span></label>
            <input id="vf-website" className="input" value={website} onChange={e => setWebsite(e.target.value)} maxLength={500}
              placeholder="e.g. www.clevedonrfc.co.uk or your club's Facebook page" />
            <p className="vf-help">The quickest way for us to see the club is real and active. Leave blank to clear it.</p>
          </div>

          <div className="vf-row">
            <div className="form-group">
              <label className="label" htmlFor="vf-phone">Your phone <span className="vf-opt">optional</span></label>
              <input id="vf-phone" className="input" value={phone} onChange={e => setPhone(e.target.value)} maxLength={40}
                placeholder="e.g. 07700 900123" inputMode="tel" />
              <p className="vf-help">A five-minute call is usually faster than email.</p>
            </div>
            <div className="form-group">
              <label className="label" htmlFor="vf-notes">Anything that helps us check <span className="vf-opt">optional</span></label>
              <input id="vf-notes" className="input" value={notes} onChange={e => setNotes(e.target.value)} maxLength={2000}
                placeholder="e.g. RFU affiliated, Somerset league, club secretary is Jo Bloggs" />
              <p className="vf-help">League, governing body, a committee contact — whatever you'd tell a new sponsor.</p>
            </div>
          </div>

          {error && <p className="vf-error"><AlertCircle size={14} /> {error}</p>}

          <div className="vf-actions">
            <button type="submit" className="btn btn-primary" disabled={submit.isPending} data-tour="verification-submit">
              <Send size={14} /> {submit.isPending ? 'Sending…' : submitted ? 'Resend for verification' : 'Submit for verification'}
            </button>
            {submitted && (
              <button type="button" className="btn btn-secondary" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>
            )}
            <span className="vf-actions-note"><ShieldCheck size={13} /> Usually verified within a day or two.</span>
          </div>
        </form>
      )}
    </div>
  );
};

export default VerificationForm;
